import { React, useEffect, useState } from "react";
import PageTitle from "../PageTitle";
import useAxios from "../../utilities/useAxios";
import {
	Table,
	Input,
	Select,
	Button,
	Space,
	Card,
	message,
	Tag,
	Tooltip,
} from "antd";
import { DownloadOutlined, UserAddOutlined } from "@ant-design/icons";
import XLSX from "xlsx-js-style";
import { useNavigate } from "react-router-dom";
import PageHeader from "../common/PageHeader";


const ViewAllMembers = () => {
	const [membersData, setMembersData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchText, setSearchText] = useState("");
	const [searchCategory, setSearchCategory] = useState("name");
	const [statusNameFilter, setStatusNameFilter] = useState("all");
	const [classificationNameFilter, setClassificationNameFilter] =
		useState("all");

	// District filters
	const [districtFilter, setDistrictFilter] = useState("all");
	const [missionaryTypeFilter, setMissionaryTypeFilter] = useState("all");
	const [zoneFilter, setZoneFilter] = useState("all");
	const [cellFilter, setCellFilter] = useState("all");

	// Department filter
	const [affiliatedDepartmentFilter, setAffiliatedDepartmentFilter] =
		useState("all");

	// District data
	const [districts, setDistricts] = useState([]);
	const [zones, setZones] = useState([]);
	const [cells, setCells] = useState([]);

	// Department data
	const [departments, setDepartments] = useState([]);

	const navigate = useNavigate();

	// Navigation functions
	const handleViewDetail = (memberId) => {
		navigate(`../view-member-detail/${memberId}`);
	};

	const handleAddMember = () => {
		navigate(`../add-member`);
	};

	useEffect(() => {
		getMembersData();
		getDistrictData();
		getDepartments();
		// Expose refresh function to window object
		window.refreshMembers = getMembersData;
		// Cleanup function
		return () => {
			delete window.refreshMembers;
		};
	}, []);

	const getMembersData = async () => {
		try {
			setLoading(true);
			const response = await useAxios.get(
				"/users/all-users/",
				{
					params: {
						include_inactive: "true",
					},
				},
			);
			if (response.status === 200) {
				setMembersData(response.data);
			}
		} catch (error) {
			message.error("성도 목록을 불러오는데 실패했습니다.");
		} finally {
			setLoading(false);
		}
	};

	const getDistrictData = async () => {
		try {
			const response = await useAxios.get(
				"/district/districts/",
			);
			if (response.status === 200) {
				setDistricts(response.data);
			}
		} catch (error) {
			console.error("District data loading failed:", error);
		}
	};

	const getDepartments = async () => {
		try {
			const response = await useAxios.get(
				"/department/departments/",
			);
			if (response.status === 200) {
				setDepartments(response.data);
			}
		} catch (error) {
			console.error("Department data loading failed:", error);
		}
	};

	const getZonesByDistrictAndMissionary = async (
		districtId,
		missionaryType,
	) => {
		try {
			const response = await useAxios.get(
				`/district/zones-by-district-missionary/${districtId}/${missionaryType}/`,
			);
			if (response.status === 200) {
				setZones(response.data);
			}
		} catch (error) {
			console.error("Zone data loading failed:", error);
		}
	};

	const getCellsByZone = async (zoneId) => {
		try {
			const response = await useAxios.get(
				`/district/cells-by-zone/${zoneId}/`,
			);
			if (response.status === 200) {
				setCells(response.data);
			}
		} catch (error) {
			console.error("Cell data loading failed:", error);
		}
	};

	const handleSearch = (value) => {
		setSearchText(value);
	};

	const handleCategoryChange = (value) => {
		setSearchCategory(value);
	};

	const handleStatusNameFilterChange = (value) => {
		setStatusNameFilter(value);
	};

	const handleClassificationNameFilterChange = (value) => {
		setClassificationNameFilter(value);
	};

	const handleDistrictFilterChange = (value) => {
		setDistrictFilter(value);
		setMissionaryTypeFilter("all");
		setZoneFilter("all");
		setCellFilter("all");
		setZones([]);
		setCells([]);
	};

	const handleMissionaryTypeFilterChange = (value) => {
		setMissionaryTypeFilter(value);
		setZoneFilter("all");
		setCellFilter("all");
		setCells([]);

		if (value !== "all" && districtFilter !== "all") {
			getZonesByDistrictAndMissionary(districtFilter, value);
		} else {
			setZones([]);
		}
	};

	const handleZoneFilterChange = (value) => {
		setZoneFilter(value);
		setCellFilter("all");

		if (value !== "all") {
			getCellsByZone(value);
		} else {
			setCells([]);
		}
	};

	const handleCellFilterChange = (value) => {
		setCellFilter(value);
	};

	const handleAffiliatedDepartmentFilterChange = (value) => {
		setAffiliatedDepartmentFilter(value);
	};

	const filteredData = membersData.filter((member) => {
		// Search filter
		if (searchText) {
			const searchValue =
				member[searchCategory]
					?.toString()
					.toLowerCase() || "";
			if (!searchValue.includes(searchText.toLowerCase())) {
				return false;
			}
		}

		// Status filter
		if (
			statusNameFilter !== "all" &&
			member.status_name !== statusNameFilter
		) {
			return false;
		}

		// For '입교인' status, only show active members
		if (statusNameFilter === "입교인" && !member.is_active) {
			return false;
		}

		// For '학습인' status, only show active members
		if (statusNameFilter === "학습인" && !member.is_active) {
			return false;
		}

		// For '유아세례교인' status, only show active members
		if (statusNameFilter === "유아세례교인" && !member.is_active) {
			return false;
		}

		// Classification filter
		if (
			classificationNameFilter !== "all" &&
			member.classification_name !== classificationNameFilter
		) {
			return false;
		}

		// District filter
		if (districtFilter !== "all") {
			const memberDistrictId = member.district_id;
			if (memberDistrictId !== parseInt(districtFilter)) {
				return false;
			}
		}

		// Missionary type filter
		if (missionaryTypeFilter !== "all") {
			const memberMissionaryType = member.missionary_type;
			if (memberMissionaryType !== missionaryTypeFilter) {
				return false;
			}
		}

		// Zone filter
		if (zoneFilter !== "all") {
			const memberZoneId = member.zone_id;
			if (memberZoneId !== parseInt(zoneFilter)) {
				return false;
			}
		}

		// Cell filter
		if (cellFilter !== "all") {
			const memberCellId = member.cell_id;
			if (memberCellId !== parseInt(cellFilter)) {
				return false;
			}
		}

		// Affiliated department filter
		if (affiliatedDepartmentFilter !== "all") {
			const memberAffiliatedDepartmentId =
				member.affiliated_department_id;
			if (
				memberAffiliatedDepartmentId !==
				parseInt(affiliatedDepartmentFilter)
			) {
				return false;
			}
		}

		return true;
	});

	const getStatusColor = (status) => {
		switch (status) {
			case "입교인":
				return "blue";
			case "학습인":
				return "green";
			case "유아세례교인":
				return "orange";
			case "실종교인":
				return "red";
			case "별세교인":
				return "default";
			default:
				return "default";
		}
	};

	const getClassificationColor = (classification) => {
		switch (classification) {
			case "성도":
				return "blue";
			case "교직원":
				return "green";
			case "교역자":
				return "purple";
			case "은퇴목사":
				return "orange";
			default:
				return "default";
		}
	};

	const formatDate = (dateString) => {
		if (!dateString) return "-";
		const isoDate = String(dateString).match(/^(\d{4}-\d{2}-\d{2})/);
		if (isoDate) return isoDate[1];
		const date = new Date(dateString);
		if (Number.isNaN(date.getTime())) return "-";
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, "0");
		const day = String(date.getDate()).padStart(2, "0");
		return `${year}-${month}-${day}`;
	};

	const downloadExcel = async () => {
		try {
			const date = new Date();
			const today = date.toISOString().split("T")[0];

			const wb = XLSX.utils.book_new();
			const headerStyle = {
				font: {
					bold: true,
					color: { rgb: "000000" },
					sz: 13,
				},
				fill: { fgColor: { rgb: "FAF4DB" } },
				alignment: {
					horizontal: "center",
					vertical: "center",
				},
				border: {
					left: {
						style: "thin",
						color: { auto: 1 },
					},
					right: {
						style: "thin",
						color: { auto: 1 },
					},
					top: {
						style: "thin",
						color: { auto: 1 },
					},
					bottom: {
						style: "thin",
						color: { auto: 1 },
					},
				},
			};

			const dataStyle = {
				font: { color: { rgb: "000000" }, sz: 11 },
				fill: { fgColor: { rgb: "FFFFFF" } },
				alignment: {
					horizontal: "center",
					vertical: "center",
				},
				border: {
					left: {
						style: "thin",
						color: { auto: 1 },
					},
					right: {
						style: "thin",
						color: { auto: 1 },
					},
					top: {
						style: "thin",
						color: { auto: 1 },
					},
					bottom: {
						style: "thin",
						color: { auto: 1 },
					},
				},
			};

			const headerRow = [
				{ v: "ID", t: "s", s: headerStyle },
				{ v: "이름", t: "s", s: headerStyle },
				{ v: "생년월일", t: "s", s: headerStyle },
				{ v: "성별", t: "s", s: headerStyle },
				{ v: "연락처", t: "s", s: headerStyle },
				{ v: "분류", t: "s", s: headerStyle },
				{ v: "상태", t: "s", s: headerStyle },
				{ v: "소속", t: "s", s: headerStyle },
				{ v: "징계상태", t: "s", s: headerStyle },
				{ v: "전출여부", t: "s", s: headerStyle },
			];

			const dataRows = filteredData.map((member) => [
				{ v: member.id ?? "-", t: "s", s: dataStyle },
				{ v: member.name || "-", t: "s", s: dataStyle },
				{
					v: formatDate(member.birthdate),
					t: "s",
					s: dataStyle,
				},
				{
					v: member.gender || "-",
					t: "s",
					s: dataStyle,
				},
				{
					v: member.phone_number || "-",
					t: "s",
					s: dataStyle,
				},
				{
					v: member.classification_name || "-",
					t: "s",
					s: dataStyle,
				},
				{
					v: member.status_name || "-",
					t: "s",
					s: dataStyle,
				},
				{
					v: member.affiliated_group || "-",
					t: "s",
					s: dataStyle,
				},
				{
					v: member.disciplinary_status || "-",
					t: "s",
					s: dataStyle,
				},
				{
					v: member.is_outmigration
						? "전출"
						: "재적",
					t: "s",
					s: dataStyle,
				},
			]);

			const ws = XLSX.utils.aoa_to_sheet([
				headerRow,
				...dataRows,
			]);
			XLSX.utils.book_append_sheet(wb, ws, "성도 목록");
			XLSX.writeFile(wb, `성도 목록_${today}.xlsx`);
		} catch (error) {
			message.error("엑셀 다운로드 중 오류가 발생했습니다.");
		}
	};

	const columns = [
		{
			title: "ID",
			dataIndex: "id",
			key: "id",
			width: 80,
			sorter: (a, b) => (a.id || 0) - (b.id || 0),
		},
		{
			title: "이름",
			dataIndex: "name",
			key: "name",
			sorter: (a, b) =>
				(a.name || "").localeCompare(b.name || ""),
			render: (text) => text || "-",
		},
		{
			title: "생년월일",
			dataIndex: "birthdate",
			key: "birthdate",
			sorter: (a, b) =>
				new Date(a.birthdate || 0) -
				new Date(b.birthdate || 0),
			render: (text) => formatDate(text),
		},
		{
			title: "성별",
			dataIndex: "gender",
			key: "gender",
			sorter: (a, b) =>
				(a.gender || "").localeCompare(b.gender || ""),
			render: (text) => text || "-",
		},
		{
			title: "연락처",
			dataIndex: "phone_number",
			key: "phone_number",
			render: (text) => text || "-",
		},
		{
			title: "분류",
			dataIndex: "classification_name",
			key: "classification_name",
			sorter: (a, b) =>
				(a.classification_name || "").localeCompare(
					b.classification_name || "",
				),
			render: (text) => (
				<Tag color={getClassificationColor(text)}>
					{text || "-"}
				</Tag>
			),
		},
		{
			title: "상태",
			dataIndex: "status_name",
			key: "status_name",
			sorter: (a, b) =>
				(a.status_name || "").localeCompare(
					b.status_name || "",
				),
			render: (text) => (
				<Tag color={getStatusColor(text)}>
					{text || "-"}
				</Tag>
			),
		},
		{
			title: "소속",
			dataIndex: "affiliated_group",
			key: "affiliated_group",
			sorter: (a, b) =>
				(a.affiliated_group || "").localeCompare(
					b.affiliated_group || "",
				),
			render: (text) => (
				<Tooltip title={text || "-"}>
					<span>{text || "-"}</span>
				</Tooltip>
			),
		},
		{
			title: "징계상태",
			dataIndex: "disciplinary_status",
			key: "disciplinary_status",
			sorter: (a, b) =>
				(a.disciplinary_status || "").localeCompare(
					b.disciplinary_status || "",
				),
			render: (text) => text || "-",
		},
		{
			title: "전출여부",
			dataIndex: "is_outmigration",
			key: "is_outmigration",
			sorter: (a, b) =>
				(a.is_outmigration || false) -
				(b.is_outmigration || false),
			render: (isOutmigration) =>
				isOutmigration ? (
					<Tag color="red">전출</Tag>
				) : (
					<Tag color="green">재적</Tag>
				),
		},
		{
			title: "상세정보",
			key: "view_detail",
			render: (_, record) => (
				<Button
					type="primary"
					onClick={() =>
						handleViewDetail(record.id)
					}
					size="small">
					상세보기
				</Button>
			),
		},
	];

	return (
		<>
			<PageTitle title="성도 목록" />
			<div className="member__container page">
				<PageHeader title="성도 목록" />
				<div className="member__header">
					<div className="member__header__toolbar">
						<Space>
							<Select
								value={
									districtFilter
								}
								style={{
									width: 120,
								}}
								onChange={
									handleDistrictFilterChange
								}
								placeholder="구역"
								options={[
									{
										value: "all",
										label: "전체 구역",
									},
									...districts.map(
										(
											district,
										) => ({
											value: district.id.toString(),
											label:
												district.name +
												"대 교구",
										}),
									),
								]}
							/>
							<Select
								value={
									missionaryTypeFilter
								}
								style={{
									width: 120,
								}}
								onChange={
									handleMissionaryTypeFilterChange
								}
								placeholder="선교구분"
								disabled={
									districtFilter ===
									"all"
								}
								options={[
									{
										value: "all",
										label: "전체 선교구분",
									},
									{
										value: "남선",
										label: "남선교회",
									},
									{
										value: "여선",
										label: "여선교회",
									},
								]}
							/>
							<Select
								value={
									zoneFilter
								}
								style={{
									width: 120,
								}}
								onChange={
									handleZoneFilterChange
								}
								placeholder="지구"
								disabled={
									missionaryTypeFilter ===
									"all"
								}
								options={[
									{
										value: "all",
										label: "전체 지구",
									},
									...zones.map(
										(
											zone,
										) => ({
											value: zone.id.toString(),
											label:
												zone.name +
												"지구",
										}),
									),
								]}
							/>
							<Select
								value={
									cellFilter
								}
								style={{
									width: 120,
								}}
								onChange={
									handleCellFilterChange
								}
								placeholder="구역"
								disabled={
									zoneFilter ===
									"all"
								}
								options={[
									{
										value: "all",
										label: "전체 구역",
									},
									...cells.map(
										(
											cell,
										) => ({
											value: cell.id.toString(),
											label:
												cell.name +
												"구역",
										}),
									),
								]}
							/>
							<Select
								value={
									affiliatedDepartmentFilter
								}
								style={{
									width: 120,
								}}
								onChange={
									handleAffiliatedDepartmentFilterChange
								}
								placeholder="소속기관"
								options={[
									{
										value: "all",
										label: "전체 소속기관",
									},
									...departments.map(
										(
											department,
										) => ({
											value: department.id.toString(),
											label: department.name,
										}),
									),
								]}
							/>
						</Space>
						<Space>
							<Select
								defaultValue="name"
								style={{
									width: 120,
								}}
								onChange={
									handleCategoryChange
								}
								options={[
									{
										value: "id",
										label: "ID",
									},
									{
										value: "name",
										label: "이름",
									},
									{
										value: "phone_number",
										label: "연락처",
									},
									{
										value: "affiliated_group",
										label: "소속",
									},
								]}
							/>
							<Input.Search
								placeholder="검색어를 입력하세요"
								allowClear
								onSearch={
									handleSearch
								}
								style={{
									width: 200,
								}}
							/>
							<Select
								defaultValue="all"
								style={{
									width: 120,
								}}
								onChange={
									handleStatusNameFilterChange
								}
								options={[
									{
										value: "all",
										label: "전체 상태",
									},
									{
										value: "입교인",
										label: "입교인",
									},
									{
										value: "학습인",
										label: "학습인",
									},
									{
										value: "유아세례교인",
										label: "유아세례교인",
									},
									{
										value: "실종교인",
										label: "실종교인",
									},
									{
										value: "별세교인",
										label: "별세교인",
									},
								]}
							/>
							<Select
								defaultValue="all"
								style={{
									width: 120,
								}}
								onChange={
									handleClassificationNameFilterChange
								}
								options={[
									{
										value: "all",
										label: "전체 분류",
									},
									{
										value: "성도",
										label: "성도",
									},
									{
										value: "교직원",
										label: "교직원",
									},
									{
										value: "교역자",
										label: "교역자",
									},
									{
										value: "은퇴목사",
										label: "은퇴목사",
									},
								]}
							/>
						</Space>
						<Space>
							<Button
								icon={
									<DownloadOutlined />
								}
								onClick={
									downloadExcel
								}>
								엑셀 다운로드
							</Button>
							<Button
								type="primary"
								icon={
									<UserAddOutlined />
								}
								onClick={() =>
									handleAddMember()
								}>
								성도 추가
							</Button>
						</Space>
					</div>
				</div>
				<div className="member__content-container">
					<Card className="surface-card member__table-card">
						<Table
							columns={columns}
							dataSource={
								filteredData
							}
							rowKey="id"
							loading={loading}
							pagination={{
								pageSize: 20,
								showSizeChanger: true,
								showQuickJumper: true,
								showTotal: (
									total,
									range,
								) =>
									`${range[0]}-${range[1]} / 총 ${total}명`,
							}}
						/>
					</Card>
				</div>
			</div>
		</>
	);
};

export default ViewAllMembers;
